import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Shield, AlertTriangle, ChevronRight,
  ChevronDown, Wrench, Flame, Scissors, Cog, Sparkles, Layers,
  Thermometer, ScanLine, Eye, FileText, Clock,
  ArrowLeft, Wind, Paintbrush, Zap, FolderOpen, CheckCircle2,
  Info, X, FlaskConical, Link2, ArrowRightToLine, Plus
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { processLibraryService } from '../services/processLibraryService';
import TemplateCreatorDrawer from './TemplateCreatorDrawer';

const CATEGORY_ICONS = {
  'Cutting': Scissors, 'Forming & Bending': Wrench, 'Welding': Flame,
  'Machining': Cog, 'Threading': Wrench, 'Finishing': Sparkles,
  'Assembly': Layers, 'Heat Treatment': Thermometer, 'Inspection & Testing': Eye,
  'NDT': ScanLine, 'Surface Preparation': Wind, 'Metallic Coatings': Layers,
  'Paint System': Paintbrush, 'Chemical Treatment': FlaskConical,
  'Powder / Specialty': Zap,
};

const DOMAIN_META = {
  fabrication: {
    label: 'Fabrication Processes',
    accent: '#38BDF8',
    bgClass: 'bg-slate-950',
    badgeClass: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
    activeSidebar: 'bg-sky-500/10 text-sky-300 border-r-2 border-sky-400',
  },
  surface_treatment: {
    label: 'Surface Treatment',
    accent: '#A78BFA',
    bgClass: 'bg-slate-950',
    badgeClass: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
    activeSidebar: 'bg-violet-500/10 text-violet-300 border-r-2 border-violet-400',
  },
};

const InfoRow = ({ label, value, highlight }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-0.5">
      <span className="text-[10px] text-slate-500 w-24 shrink-0">{label}</span>
      <span className={`text-[10px] ${highlight ? 'text-cyan-400' : 'text-slate-300'}`}>{value}</span>
    </div>
  );
};

const Section = ({ title, icon, children, accent }) => (
  <div className={`rounded-lg border p-3 ${accent ? 'border-amber-500/20 bg-amber-500/[0.02]' : 'border-slate-700/50'}`}>
    <div className="flex items-center gap-1.5 mb-2">
      {icon}
      <h3 className="text-[10px] font-semibold text-slate-300 uppercase tracking-wider">{title}</h3>
    </div>
    {children}
  </div>
);

const CategorySidebar = ({ categories, selectedCategory, onSelect, stats, domain }) => {
  const [expanded, setExpanded] = useState({});
  const meta = DOMAIN_META[domain];
  const catEntries = Object.entries(categories || {});

  return (
    <div className="w-56 shrink-0 border-r border-slate-700/50 overflow-y-auto" data-testid="category-sidebar">
      <button onClick={() => onSelect(null)} data-testid="category-all"
        className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${!selectedCategory ? meta.activeSidebar : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`}>
        <FolderOpen className="w-3.5 h-3.5" />
        <span className="font-medium">All Processes</span>
        <span className="ml-auto text-[10px] text-slate-500">
          {domain === 'fabrication' ? stats?.fabrication_total : stats?.surface_treatment_total || 0}
        </span>
      </button>
      <div className="h-px bg-slate-700/30 my-1" />
      {catEntries.map(([cat, info]) => {
        const Icon = CATEGORY_ICONS[cat] || FileText;
        const isActive = selectedCategory === cat;
        const isExpanded = expanded[cat];
        const catStat = stats?.by_category?.[`${domain}:${cat}`];
        return (
          <div key={cat}>
            <button
              onClick={() => { onSelect(cat); if (info.subcategories?.length) setExpanded(p => ({ ...p, [cat]: !p[cat] })); }}
              data-testid={`category-${cat.replace(/[^a-zA-Z]/g, '-')}`}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] transition-colors ${isActive ? 'bg-slate-800 text-slate-100' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'}`}>
              <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: isActive ? meta.accent : undefined }} />
              <span className="truncate font-medium">{cat}</span>
              {info.is_special_process && <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />}
              <span className="ml-auto text-[9px] text-slate-600 font-mono">{catStat?.total || 0}</span>
              {info.subcategories?.length > 0 && (isExpanded ? <ChevronDown className="w-3 h-3 shrink-0 text-slate-600" /> : <ChevronRight className="w-3 h-3 shrink-0 text-slate-600" />)}
            </button>
            {isExpanded && info.subcategories?.map(sub => (
              <div key={sub} className="pl-9 pr-3 py-0.5 text-[10px] text-slate-500 truncate">{sub}</div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const ProcessCard = ({ process, domain, onClick }) => {
  const meta = DOMAIN_META[domain];
  const qc = process.quality_control || {};
  const def = process.definition || {};
  const cost = process.cost_model || {};
  const isSpecial = qc.is_special_process;
  const bomUsage = (process.applied_to_bom_lines || []).length;
  const isBomOriginated = process.origin === 'bom_originated';

  return (
    <div onClick={() => onClick(process)} data-testid={`process-card-${process.process_code}`}
      className={`group border rounded-lg p-3 transition-all cursor-pointer hover:shadow-lg hover:shadow-black/20 ${isSpecial ? 'border-amber-500/30 bg-amber-500/5 hover:border-amber-400/50' : isBomOriginated ? 'border-blue-500/30 bg-blue-500/5 hover:border-blue-400/50' : 'border-slate-700/50 bg-slate-900/50 hover:border-slate-600'}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="font-mono text-[10px] text-slate-500">{process.process_code}</span>
            {isSpecial && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-amber-500/10 text-amber-400 border-amber-500/30">SPECIAL</Badge>}
            {isBomOriginated && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-blue-500/10 text-blue-400 border-blue-500/30">BOM ORIGINATED</Badge>}
            {process.status === 'draft' && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-orange-500/10 text-orange-400 border-orange-500/30">DRAFT</Badge>}
          </div>
          <h4 className="text-xs font-semibold text-slate-100 truncate leading-tight">{process.process_name}</h4>
        </div>
        <Badge variant="outline" className={`text-[8px] px-1.5 py-0 shrink-0 ${meta.badgeClass}`}>{process.process_category}</Badge>
      </div>
      {def.description && <p className="text-[10px] text-slate-500 line-clamp-2 mb-2 leading-relaxed">{def.description}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        {def.standard_ref && <span className="text-[9px] font-mono text-cyan-500/70 bg-cyan-500/5 px-1.5 py-0.5 rounded">{def.standard_ref}</span>}
        {qc.inspection_type && qc.inspection_type !== 'visual' && <span className="text-[9px] text-slate-500">QC: {qc.inspection_type}</span>}
        {bomUsage > 0 && (
          <span className="text-[9px] text-blue-400/70 flex items-center gap-0.5">
            <Link2 className="w-2.5 h-2.5" /> {bomUsage} BOM ref{bomUsage > 1 ? 's' : ''}
          </span>
        )}
        {cost.rate > 0 && <span className="text-[9px] font-mono text-emerald-500/70 ml-auto">{cost.rate?.toLocaleString()}/{cost.unit_label}</span>}
      </div>
      {process.as9100d_clauses?.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1">
          <Shield className="w-2.5 h-2.5 text-slate-600" />
          {process.as9100d_clauses.map(c => <span key={c} className="text-[8px] font-mono text-slate-600">{c}</span>)}
        </div>
      )}
    </div>
  );
};

const ProcessDetailDrawer = ({ process, onClose, domain, bomContext, onApply, applying }) => {
  if (!process) return null;
  const meta = DOMAIN_META[domain];
  const qc = process.quality_control || {};
  const def = process.definition || {};
  const cost = process.cost_model || {};
  const isSpecial = qc.is_special_process;
  const bomUsage = (process.applied_to_bom_lines || []).length;
  const canApply = bomContext?.isDraft && bomContext?.lineId && bomContext?.lineMakeBuy === 'Make';

  return (
    <div className="fixed inset-y-0 right-0 w-[480px] bg-slate-900 border-l border-slate-700/50 z-50 overflow-y-auto shadow-2xl shadow-black/40" data-testid="process-detail-drawer">
      <div className={`p-4 border-b border-slate-700/50 ${isSpecial ? 'bg-amber-500/5' : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300" data-testid="close-drawer"><X className="w-4 h-4" /></button>
          <div className="flex items-center gap-2">
            {canApply && (
              <Button size="sm" onClick={() => onApply(process.process_code)}
                disabled={applying}
                data-testid="apply-to-line-btn"
                className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-500 text-white gap-1">
                <ArrowRightToLine className="w-3 h-3" />
                {applying ? 'Applying...' : 'Apply to Line'}
              </Button>
            )}
            <Badge variant="outline" className={`text-[9px] ${process.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/10 text-amber-400 border-amber-500/30'}`}>{process.status?.toUpperCase()}</Badge>
            <span className="text-[9px] text-slate-600 font-mono">v{process.version}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-[10px] text-slate-500">{process.process_code}</span>
          {isSpecial && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-amber-500/10 text-amber-400 border-amber-500/30">SPECIAL PROCESS — AS 9100D 8.5.1.2</Badge>}
          {process.origin === 'bom_originated' && <Badge variant="outline" className="text-[8px] px-1 py-0 bg-blue-500/10 text-blue-400 border-blue-500/30">BOM ORIGINATED</Badge>}
        </div>
        <h2 className="text-sm font-semibold text-slate-100">{process.process_name}</h2>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className={`text-[9px] ${meta.badgeClass}`}>{process.process_category}</Badge>
          {process.process_subcategory && <span className="text-[10px] text-slate-500">{process.process_subcategory}</span>}
          {bomUsage > 0 && (
            <span className="text-[9px] text-blue-400/70 flex items-center gap-0.5 ml-auto">
              <Link2 className="w-3 h-3" /> Used in {bomUsage} BOM line{bomUsage > 1 ? 's' : ''}
            </span>
          )}
        </div>
        {!canApply && bomContext?.lineId && bomContext?.isDraft && bomContext?.lineMakeBuy !== 'Make' && (
          <div className="mt-2 text-[9px] text-slate-500 bg-slate-800/50 rounded px-2 py-1">
            Processes can only be applied to Make-classified parts.
          </div>
        )}
        {!canApply && bomContext && !bomContext.lineId && (
          <div className="mt-2 text-[9px] text-slate-500 bg-slate-800/50 rounded px-2 py-1">
            Select a Make part in the BOM tree to apply this process.
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        <Section title="Process Definition" icon={<Info className="w-3.5 h-3.5" />}>
          {def.description && <p className="text-[11px] text-slate-400 leading-relaxed mb-2">{def.description}</p>}
          {def.standard_ref && <InfoRow label="Standard" value={def.standard_ref} highlight />}
          {def.applicable_materials?.length > 0 && def.applicable_materials[0] !== '*' && <InfoRow label="Materials" value={def.applicable_materials.join(', ')} />}
          {def.applicable_profiles?.length > 0 && def.applicable_profiles[0] !== '*' && <InfoRow label="Profiles" value={def.applicable_profiles.join(', ')} />}
          {def.equipment_required?.length > 0 && <InfoRow label="Equipment" value={def.equipment_required.join(', ')} />}
          {def.parameters && Object.keys(def.parameters).length > 0 && (
            <div className="mt-2">
              <span className="text-[9px] text-slate-600 uppercase tracking-wider font-medium">Parameters</span>
              <div className="mt-1 grid grid-cols-2 gap-1">
                {Object.entries(def.parameters).map(([k, v]) => (
                  <div key={k} className="text-[10px] bg-slate-800/50 rounded px-2 py-1">
                    <span className="text-slate-500">{k.replace(/_/g, ' ')}:</span> <span className="text-slate-300">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section title="Quality Control" icon={<Shield className="w-3.5 h-3.5 text-amber-400" />} accent={isSpecial}>
          {isSpecial && qc.special_process_justification && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2 mb-2">
              <div className="text-[9px] text-amber-400 font-medium mb-0.5">Special Process Justification</div>
              <p className="text-[10px] text-amber-300/80 leading-relaxed">{qc.special_process_justification}</p>
            </div>
          )}
          <InfoRow label="QC Involvement" value={qc.qc_involvement} />
          <InfoRow label="Sampling" value={qc.sampling_method} />
          <InfoRow label="Inspection" value={qc.inspection_type} />
          {qc.pass_fail_criteria && <InfoRow label="Pass/Fail" value={qc.pass_fail_criteria} />}
          {qc.operator_qualification && <InfoRow label="Operator Qual." value={qc.operator_qualification} highlight />}
          {qc.records_required?.length > 0 && <InfoRow label="Records" value={qc.records_required.join(', ')} />}
        </Section>

        {cost.rate > 0 && (
          <Section title="Cost Model" icon={<span className="text-emerald-400 text-xs font-bold">&#8377;</span>}>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-mono font-bold text-emerald-400">{cost.rate?.toLocaleString()}</span>
              <span className="text-[10px] text-slate-500">/{cost.unit_label} ({cost.model_type?.replace(/_/g, ' ')})</span>
            </div>
          </Section>
        )}

        {process.as9100d_clauses?.length > 0 && (
          <Section title="AS 9100D Compliance" icon={<CheckCircle2 className="w-3.5 h-3.5 text-cyan-400" />}>
            <div className="flex flex-wrap gap-1.5">
              {process.as9100d_clauses.map(c => (
                <span key={c} className="text-[10px] font-mono bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded px-2 py-0.5">Clause {c}</span>
              ))}
            </div>
          </Section>
        )}

        <Section title="Review Schedule" icon={<Clock className="w-3.5 h-3.5" />}>
          <InfoRow label="Interval" value={`${process.review_schedule?.review_interval_months || 12} months`} />
          <InfoRow label="Last Review" value={process.review_schedule?.last_review_date || 'Not yet reviewed'} />
          <InfoRow label="Next Review" value={process.review_schedule?.next_review_date || 'Pending'} />
        </Section>

        <div className="text-[9px] text-slate-600 space-y-0.5 pt-2 border-t border-slate-800">
          <div>Origin: {process.origin}</div>
          <div>Created: {process.created_at?.split('T')[0]}</div>
          {process.tags?.length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap">
              {process.tags.map(t => <span key={t} className="bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded text-[8px]">{t}</span>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const QuickStatsBar = ({ stats, domain }) => {
  const meta = DOMAIN_META[domain];
  const total = domain === 'fabrication' ? stats?.fabrication_total : stats?.surface_treatment_total;
  const catCount = Object.keys(stats?.by_category || {}).filter(k => k.startsWith(domain)).length;
  const activeCount = Object.values(stats?.by_category || {}).filter(v => v.domain === domain).reduce((a, v) => a + (v.active || 0), 0);
  const specialCount = Object.values(stats?.by_category || {}).filter(v => v.domain === domain).reduce((a, v) => a + (v.special || 0), 0);

  return (
    <div className="flex items-center gap-4 px-4 py-2 border-b border-slate-700/30 bg-slate-900/80" data-testid="quick-stats-bar">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-500">Total</span>
        <span className="text-sm font-mono font-bold" style={{ color: meta.accent }}>{total || 0}</span>
      </div>
      <div className="h-3 w-px bg-slate-700/50" />
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-500">Categories</span>
        <span className="text-xs font-mono text-slate-300">{catCount}</span>
      </div>
      <div className="h-3 w-px bg-slate-700/50" />
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-slate-500">Active</span>
        <span className="text-xs font-mono text-emerald-400">{activeCount}</span>
      </div>
      <div className="h-3 w-px bg-slate-700/50" />
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="w-3 h-3 text-amber-500" />
        <span className="text-[10px] text-slate-500">Special</span>
        <span className="text-xs font-mono text-amber-400">{specialCount}</span>
      </div>
    </div>
  );
};

export default function ProcessLibraryPage({ domain = 'fabrication', onBack, bomContext, onProcessApplied }) {
  const [processes, setProcesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState({});
  const [stats, setStats] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [specialFilter, setSpecialFilter] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [applying, setApplying] = useState(false);
  const [showTemplateCreator, setShowTemplateCreator] = useState(false);

  const meta = DOMAIN_META[domain];

  const handleApplyToLine = useCallback(async (processCode) => {
    if (!bomContext?.bomId || !bomContext?.revisionId || !bomContext?.lineId) {
      toast.error('No BOM line selected');
      return;
    }
    setApplying(true);
    try {
      await processLibraryService.applyToLine({
        process_code: processCode,
        bom_id: bomContext.bomId,
        revision_id: bomContext.revisionId,
        line_id: bomContext.lineId,
      });
      toast.success(`Process applied to BOM line`);
      setSelectedProcess(null);
      if (onProcessApplied) onProcessApplied();
    } catch (e) {
      toast.error(e.message || 'Failed to apply process');
    } finally {
      setApplying(false);
    }
  }, [bomContext, onProcessApplied]);

  useEffect(() => {
    const load = async () => {
      try {
        const [catData, statsData] = await Promise.all([
          processLibraryService.getCategories(domain),
          processLibraryService.getStats(domain),
        ]);
        setCategories(catData[domain] || {});
        setStats(statsData);
      } catch (e) { console.error('Failed to load metadata:', e); }
    };
    load();
  }, [domain]);

  const fetchProcesses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await processLibraryService.listProcesses({
        domain, category: selectedCategory, search, isSpecial: specialFilter, limit: 200,
      });
      setProcesses(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load processes'); }
    finally { setLoading(false); }
  }, [domain, selectedCategory, search, specialFilter]);

  useEffect(() => { fetchProcesses(); }, [fetchProcesses]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const grouped = useMemo(() => {
    const map = {};
    processes.forEach(p => {
      const cat = p.process_category || 'Other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(p);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [processes]);

  return (
    <div className={`h-full flex flex-col ${meta.bgClass} text-white`} data-testid={`process-library-${domain}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 shrink-0">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="text-slate-500 hover:text-slate-300 transition-colors" data-testid="process-library-back">
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div>
            <h1 className="text-sm font-semibold text-slate-100">{meta.label} Library</h1>
            <p className="text-[10px] text-slate-500 mt-0.5">{total} processes — AS 9100D compliant process management</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowTemplateCreator(true)}
            data-testid="create-template-btn"
            className="text-[10px] h-7 text-cyan-400 border-cyan-600/30 hover:bg-cyan-900/20 hover:text-cyan-300">
            <Plus className="w-3 h-3 mr-1" /> Create Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowTemplateCreator(true)}
            data-testid="manage-templates-btn"
            className="text-[10px] h-7 text-slate-400 border-slate-600/30 hover:bg-slate-700/30 hover:text-white">
            <Layers className="w-3 h-3 mr-1" /> Manage Templates
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSpecialFilter(prev => prev === true ? null : true)}
            data-testid="filter-special"
            className={`text-[10px] h-7 ${specialFilter === true ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'text-slate-400 border-slate-700 hover:text-slate-200'}`}>
            <AlertTriangle className="w-3 h-3 mr-1" /> Special Only
          </Button>
          <div className="relative w-52">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <Input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search processes..."
              className="pl-8 h-7 text-[11px] bg-slate-800/50 border-slate-700/50" data-testid="process-search" />
          </div>
        </div>
      </div>

      {stats && <QuickStatsBar stats={stats} domain={domain} />}

      <div className="flex flex-1 overflow-hidden">
        <CategorySidebar categories={categories} selectedCategory={selectedCategory} onSelect={setSelectedCategory} stats={stats} domain={domain} />

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="text-[11px] text-slate-500">Loading processes...</div></div>
          ) : processes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-slate-500">
              <Search className="w-6 h-6 mb-2 opacity-30" /><span className="text-xs">No processes found</span>
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map(([cat, items]) => {
                const Icon = CATEGORY_ICONS[cat] || FileText;
                const catStat = stats?.by_category?.[`${domain}:${cat}`];
                return (
                  <div key={cat} data-testid={`category-group-${cat.replace(/[^a-zA-Z]/g, '-')}`}>
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-800">
                      <Icon className="w-3.5 h-3.5" style={{ color: meta.accent }} />
                      <h3 className="text-xs font-semibold text-slate-300">{cat}</h3>
                      <span className="text-[9px] text-slate-600 font-mono">{items.length}</span>
                      {catStat?.special > 0 && <span className="text-[8px] text-amber-500 ml-1">{catStat.special} special</span>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                      {items.map(p => <ProcessCard key={p.process_code} process={p} domain={domain} onClick={setSelectedProcess} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedProcess && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setSelectedProcess(null)} />
          <ProcessDetailDrawer process={selectedProcess} onClose={() => setSelectedProcess(null)} domain={domain}
            bomContext={bomContext} onApply={handleApplyToLine} applying={applying} />
        </>
      )}

      {showTemplateCreator && (
        <TemplateCreatorDrawer
          onClose={() => setShowTemplateCreator(false)}
          onCreated={() => { toast.success('Template created'); setShowTemplateCreator(false); }}
        />
      )}
    </div>
  );
}
